import {
  MutationFunction,
  MutationKey,
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationOptions,
} from '@tanstack/react-query';

import { ApiResponseError } from '@api/apiUtils/ApiResponseError';

import type {
  CustomUseMutationFunction,
  UseCustomMutationResult,
  SetDataType,
} from '@utils/hooks/mutation.types';

export const useCustomMutation = <
  TVariables = unknown, // переменные для функции
  TData = unknown, // то что должно быть возвращено
  TContext = unknown,
  TError = ApiResponseError,
>(
  mutationKey: MutationKey,
  mutationFunction: MutationFunction<TData, TVariables>,
  options?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseCustomMutationResult<TVariables, TData, TError, TContext> => {
  const query = useQuery<SetDataType<TData>, SetDataType<TError>>({
    queryKey: ['CustomMutation', mutationKey],
    queryFn: async () => false as unknown as SetDataType<TData>,
    retry: false,
    staleTime: Infinity,
    gcTime: 10_000,
  });

  const queryClient = useQueryClient();

  const { mutate: originMutate, ...rest } = useMutation<
    TData,
    TError,
    TVariables,
    TContext
  >({
    mutationKey,
    mutationFn: (...variables) => {
      queryClient.setQueryData(['CustomMutation', mutationKey], {
        payload: false,
        status: 'pending',
      });
      return mutationFunction(...variables);
    },
    ...options,
    onSuccess: (data, variables, context, mutationFunctionContext) => {
      queryClient.setQueryData<SetDataType<TData>>(
        ['CustomMutation', mutationKey],
        {
          payload: data,
          status: 'success',
        },
      );
      options?.onSuccess?.(data, variables, context, mutationFunctionContext);
    },
    onError: (error, variables, context, mutationFunctionContext) => {
      queryClient.setQueryData<SetDataType<TError>>(
        ['CustomMutation', mutationKey],
        {
          payload: error,
          status: 'error',
        },
      );

      options?.onError?.(error, variables, context, mutationFunctionContext);
    },
  });

  const mutate: CustomUseMutationFunction<
    TVariables,
    TData,
    TError,
    TContext
  > = (
    variables: TVariables,
    callOptions?: Omit<
      UseMutationOptions<TData, TError, TVariables, TContext>,
      'mutationKey' | 'mutationFn'
    >,
  ) => {
    return originMutate(variables, callOptions);
  };

  // We need typecasting here due the ADT about the mutation result, and as
  // we're using a data not related to the mutation result. The typescript
  // can't infer the type correctly.

  return {
    mutate,
    ...rest,
    data: query.data?.payload as TData,
    isSuccess: query.data?.status === 'success',
    isPending: query.data?.status === 'pending',
    error: query.data?.payload, // error saved as query data
    isError: query.data?.status === 'error', // error saved as query data
  } as UseCustomMutationResult<TVariables, TData, TError, TContext>;
};
